import type React from 'react';
import type { PerUnitProduct } from '@vy/services';
import { filterFixedProducts, isMeteredCreditProductOnly } from '@vy/services';
import type { CatalogProducts, Currency, FixedProduct, Plan, UsageProduct } from '@vy/types';
import { isEmptyArray } from '@vy/utils';
import { ContractExpandableSection } from '../ContractExpandableSection/ContractExpandableSection';
import type { OnEditAnrokHandler } from '../shared/anrok-cell-types';
import { ContractFixedFeesTable } from './components/ContractFixedFeesTable';
import { getContractFixedFeesColumns } from './contract-fixed-fees-utils/get-contract-fixed-fees-columns-keys';
import {
  buildFixedFeesRows,
  buildMeteredCreditRows,
} from './contract-fixed-fees-utils/get-contract-fixed-fees-rows';

type ContractFixedFeesProps = {
  plan: Plan;
  currency: Currency;
  isAnrokEnabled?: boolean;
  isConnactEnabled?: boolean;
  catalogProductsById?: Map<string, CatalogProducts>;
  onEditErp?: (product: FixedProduct | PerUnitProduct | UsageProduct, productName: string) => void;
  onEditAnrok?: OnEditAnrokHandler;
};

export const ContractFixedFees: React.FC<ContractFixedFeesProps> = ({
  plan,
  currency,
  isAnrokEnabled = false,
  isConnactEnabled = false,
  catalogProductsById,
  onEditErp,
  onEditAnrok,
}) => {
  const fixedProducts = filterFixedProducts<FixedProduct>(plan.products);
  const meteredCredits = plan.products.filter<PerUnitProduct>(isMeteredCreditProductOnly);
  const columns = getContractFixedFeesColumns(currency, { isAnrokEnabled, isConnactEnabled });
  const rows = buildFixedFeesRows(fixedProducts, plan, currency, catalogProductsById);
  const meteredCreditsRows = buildMeteredCreditRows(meteredCredits, catalogProductsById);
  const discountAndCreditsRows = [...rows, ...meteredCreditsRows];

  if (isEmptyArray(discountAndCreditsRows)) {
    return null;
  }

  return (
    <ContractExpandableSection
      sx={{ padding: '0px 6px' }}
      title="Fixed fees"
      count={discountAndCreditsRows.length}
    >
      <ContractFixedFeesTable
        rows={discountAndCreditsRows}
        columns={columns}
        currency={currency}
        onEditErp={onEditErp}
        onEditAnrok={onEditAnrok}
      />
    </ContractExpandableSection>
  );
};
